import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  TablePagination,
  Grid,
} from "@mui/material";
import {
  Business as BusinessIcon,
  Work as WorkIcon,
  Warning as WarningIcon,
  Assignment as AssignmentIcon,
  MoreHoriz as MoreIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { UserRole, useGetOpsStats } from "@taxassist/api-client";
import { useAuth } from "../../contexts/AuthContext";
import { StatusBadge } from "../../components/jobs/StatusBadge";
import { UserDisplay } from "../../components/common/UserDisplay";
import { getJobEtaDisplay } from "../../utils/jobUtils";

interface StatCard {
  title: string;
  value: string | number;
  icon: React.ReactElement;
  color: string;
}

export const OpsDashboard: React.FC = () => {
  const { user, isAuthenticated } = useAuth();

  // Dialog state for overdue jobs
  const [overdueDialogOpen, setOverdueDialogOpen] = useState(false);
  const [overdueDialogPage, setOverdueDialogPage] = useState(0);
  const [overdueDialogPageSize, setOverdueDialogPageSize] = useState(25);

  // Check if user has access
  const hasAccess =
    user?.role === UserRole.admin || user?.role === UserRole.reviewer;

  // Fetch dashboard statistics (no limits!)
  const {
    data: statsResponse,
    isLoading: statsLoading,
    error: statsError,
  } = useGetOpsStats({
    query: {
      queryKey: ["opsStats"],
      enabled: isAuthenticated && hasAccess,
      staleTime: 30000, // 30 seconds
      refetchInterval: 60000, // Auto-refresh every minute
    },
  });

  // Show unauthorized message if user doesn't have access
  if (!isAuthenticated || !hasAccess) {
    return (
      <Box p={3}>
        <Alert severity="warning">
          Unauthorized - You need REVIEWER or ADMIN access to view this page
        </Alert>
      </Box>
    );
  }

  // Show loading state
  if (statsLoading) {
    return (
      <Box
        p={3}
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Show error state
  if (statsError) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Error loading dashboard data:{" "}
          {statsError instanceof Error ? statsError.message : "Unknown error"}
        </Alert>
      </Box>
    );
  }

  // Extract stats from response
  const stats = statsResponse?.stats;
  if (!stats) {
    return (
      <Box p={3}>
        <Alert severity="warning">No dashboard data available</Alert>
      </Box>
    );
  }

  // Get data from stats response
  const overdueJobs = stats.overdueJobs || [];

  // Helper function to calculate how overdue a job is
  const getOverdueDuration = (overdueDays: number) => {
    if (overdueDays === 1) return "1 day";
    return `${overdueDays} days`;
  };

  // Assigned jobs by user from stats
  const jobsByUser = stats.assignedJobsByUser || {};

  // Create priority stats array with most important metrics
  const priorityStats: StatCard[] = [
    {
      title: "Active Jobs",
      value: stats.totalActiveJobs,
      icon: <WorkIcon />,
      color: "#ff9800",
    },
    {
      title: "Overdue Jobs",
      value: stats.totalOverdueJobs,
      icon: <WarningIcon />,
      color: "#f44336",
    },
  ];

  // Create secondary stats array with platform metrics (de-emphasized)
  const secondaryStats: StatCard[] = [
    {
      title: "Total Firms",
      value: stats.totalFirms,
      icon: <BusinessIcon />,
      color: "#3f51b5",
    },
  ];

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Ops Dashboard
      </Typography>

      {/* Top Row Stats - Priority metrics on left, platform metrics on right */}
      <Grid container spacing={3} style={{ marginBottom: 24 }}>
        {/* Priority Stats - Left Side */}
        {priorityStats.map((stat) => (
          <Grid key={stat.title} size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Box>
                    <Typography
                      color="text.secondary"
                      gutterBottom
                      variant="body2"
                    >
                      {stat.title}
                    </Typography>
                    <Typography variant="h5">{stat.value}</Typography>
                  </Box>
                  <Box
                    sx={{
                      backgroundColor: stat.color,
                      borderRadius: "50%",
                      p: 1.5,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Box
                      component="span"
                      sx={{ color: "white", display: "flex" }}
                    >
                      {stat.icon}
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}

        {/* Secondary Stats - Right Side (De-emphasized) */}
        {secondaryStats.map((stat) => (
          <Grid key={stat.title} size={{ xs: 12, sm: 6, md: 3 }}>
            <Card sx={{ opacity: 0.7 }}>
              <CardContent>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Box>
                    <Typography
                      color="text.secondary"
                      gutterBottom
                      variant="body2"
                    >
                      {stat.title}
                    </Typography>
                    <Typography variant="h5" color="text.secondary">
                      {stat.value}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      backgroundColor: stat.color,
                      borderRadius: "50%",
                      p: 1.5,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Box
                      component="span"
                      sx={{ color: "white", display: "flex" }}
                    >
                      {stat.icon}
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Equal Width Row - Overdue Jobs and Assigned Jobs */}
      <Grid container spacing={3} style={{ marginBottom: 24 }}>
        {/* Overdue Jobs */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3, minHeight: 400 }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Typography
                variant="h6"
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <WarningIcon color="error" />
                Overdue Jobs ({overdueJobs.length})
              </Typography>
              {overdueJobs.length > 5 && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setOverdueDialogOpen(true)}
                  endIcon={<MoreIcon />}
                >
                  View All {overdueJobs.length}
                </Button>
              )}
            </Box>
            {overdueJobs.length === 0 ? (
              <Box textAlign="center" py={4}>
                <Typography variant="body1" color="text.secondary">
                  🎉 No overdue jobs! Great work!
                </Typography>
              </Box>
            ) : (
              <TableContainer sx={{ maxHeight: 300, overflow: "auto" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Client</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Assignee</TableCell>
                      <TableCell>Overdue By</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {overdueJobs.slice(0, 5).map((job) => (
                      <TableRow key={job.jobId}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {job.clientName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {job.firmId}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <StatusBadge
                            status={job.status}
                            statusMapping={null}
                            job={job as any}
                          />
                        </TableCell>
                        <TableCell>
                          {job.assignedReviewer ? (
                            <UserDisplay
                              userId={job.assignedReviewer}
                              variant="body2"
                              color="textPrimary"
                              contextFirmId={job.firmId}
                              fallback="Unknown"
                            />
                          ) : (
                            <Chip
                              label="Unassigned"
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={`${getOverdueDuration(job.overdueDays)} ago`}
                            size="small"
                            color="error"
                            variant="outlined"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>

        {/* Assigned Jobs by User */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3, minHeight: 400 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <AssignmentIcon />
              Assigned Jobs by User
            </Typography>
            {Object.keys(jobsByUser).length === 0 ? (
              <Box textAlign="center" py={4}>
                <Typography variant="body2" color="text.secondary">
                  No assigned jobs found
                </Typography>
              </Box>
            ) : (
              <TableContainer sx={{ maxHeight: 320, overflow: "auto" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>User</TableCell>
                      <TableCell align="right">Total Jobs</TableCell>
                      <TableCell align="right">In Progress</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(jobsByUser)
                      .sort(
                        ([, a], [, b]) =>
                          (b.totalJobs || 0) - (a.totalJobs || 0),
                      ) // Sort by total jobs descending
                      .map(([userId, userData]) => (
                        <TableRow key={userId}>
                          <TableCell>
                            <UserDisplay
                              userId={userId}
                              variant="body2"
                              color="textPrimary"
                              fallback={userData.userName}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="h6">
                              {userData.totalJobs || 0}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" color="text.secondary">
                              {(userData.jobsByStatus?.TRIAGE || 0) +
                                (userData.jobsByStatus?.SUBMITTED || 0) +
                                (userData.jobsByStatus?.PROCESSING || 0)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Overdue Jobs Dialog */}
      <Dialog
        open={overdueDialogOpen}
        onClose={() => setOverdueDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box display="flex" alignItems="center" gap={1}>
              <WarningIcon color="error" />
              All Overdue Jobs ({overdueJobs.length})
            </Box>
            <IconButton onClick={() => setOverdueDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <TableContainer sx={{ maxHeight: 500 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Client</TableCell>
                  <TableCell>Firm</TableCell>
                  <TableCell>Filing Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Assignee</TableCell>
                  <TableCell>Overdue By</TableCell>
                  <TableCell>Original ETA</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {overdueJobs
                  .slice(
                    overdueDialogPage * overdueDialogPageSize,
                    overdueDialogPage * overdueDialogPageSize +
                      overdueDialogPageSize,
                  )
                  .map((job) => (
                    <TableRow key={job.jobId}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {job.clientName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{job.firmId}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">N/A</Typography>
                      </TableCell>
                      <TableCell>
                        <StatusBadge
                          status={job.status}
                          statusMapping={null}
                          job={job as any}
                        />
                      </TableCell>
                      <TableCell>
                        {job.assignedReviewer ? (
                          <UserDisplay
                            userId={job.assignedReviewer}
                            variant="body2"
                            color="textPrimary"
                            contextFirmId={job.firmId}
                            fallback="Unknown"
                          />
                        ) : (
                          <Chip
                            label="Unassigned"
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={`${getOverdueDuration(job.overdueDays)} ago`}
                          size="small"
                          color="error"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {getJobEtaDisplay(job)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={overdueJobs.length}
            page={overdueDialogPage}
            onPageChange={(_, newPage) => setOverdueDialogPage(newPage)}
            rowsPerPage={overdueDialogPageSize}
            onRowsPerPageChange={(e) => {
              setOverdueDialogPageSize(parseInt(e.target.value, 10));
              setOverdueDialogPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Jobs per page:"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOverdueDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
